// @spec BCP-STR-007
// Edit BCP plan route page
'use client';

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PLAN_TYPE_LABELS } from '@/features/bcp/types';
import { fetchBCPPlan } from '@/features/bcp/api/plans';
import { EditPlanForm } from '@/features/bcp/components/EditPlanForm';
import type { BCPPlan } from '@/features/bcp/types/index';
import { useOrganizations } from '@/hooks/use-organizations';
import { createApiClient } from '@/lib/api-client';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/bcp/plans/$planId/edit')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.editPlan'),
  }),
  component: EditPlanPage,
});

function EditPlanPage() {
  const { t } = useTranslation('bcp');
  const { planId } = Route.useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { organizations } = useOrganizations();

  const activeOrg = organizations.find((org) => org.isActive);
  const orgId = activeOrg?.id ?? '';

  const { data: plan, isLoading } = useQuery<BCPPlan>({
    queryKey: ['bcp', 'plans', orgId, planId],
    queryFn: () => {
      const apiClient = createApiClient({ getAuthToken: getToken });
      return fetchBCPPlan(apiClient, orgId, planId) as unknown as BCPPlan;
    },
    enabled: Boolean(orgId) && Boolean(planId),
  });

  // Redirect if plan cannot be edited
  useEffect(() => {
    if (!plan) return;
    if (plan.status === 'archived' || plan.status === 'pending_approval') {
      toast.error(
        plan.status === 'archived'
          ? t('bcp:routes.archivedCannotBeEdited')
          : t('bcp:routes.withdrawBeforeEditing'),
      );
      void navigate({ to: '/bcp/plans/$planId', params: { planId } });
    }
  }, [plan, planId, navigate]);

  if (isLoading || !plan) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-3xl space-y-6">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2"
            onClick={() => void navigate({ to: '/bcp/plans/$planId', params: { planId } })}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('bcp:routes.backToPlan')}
          </Button>
          <h1 className="text-2xl font-semibold">{t('bcp:routes.editPlan')}</h1>
          <p className="text-muted-foreground">
            {PLAN_TYPE_LABELS[plan.planType]} &middot; v{plan.version}
          </p>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('bcp:routes.planDetails')}</CardTitle>
            <CardDescription>
              Update plan information. Only changed fields will be saved.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <EditPlanForm plan={plan} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
